import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { convertDate, calculateProgress, formatNextActivity } from './helper/labHelpers';

const fetchSize = 25;

/**
 * Hook per recuperare e processare i dati dei laboratori in modo paginato.
 * @param {{columnFilters: Array, globalFilter: string, sorting: Array, isAdmin: boolean}} options - Opzioni di filtro e ordinamento.
 * @returns L'output di useInfiniteQuery più i dati processati.
 */
export const useLabData = ({ columnFilters, globalFilter, sorting, isAdmin }) => {
    const queryResult = useInfiniteQuery({
        queryKey: ['table-data', columnFilters, globalFilter, sorting, isAdmin],
        queryFn: async ({ pageParam = 0 }) => {
            const url = new URL('/auth/reqLab/getAllLabBatch', process.env.REACT_APP_URL_GET_ALL_LAB_BATCH);
            url.searchParams.set('start', `${pageParam * fetchSize}`);
            url.searchParams.set('size', `${fetchSize}`);
            url.searchParams.set('filters', JSON.stringify(columnFilters ?? []));
            url.searchParams.set('globalFilter', globalFilter ?? '');
            url.searchParams.set('sorting', JSON.stringify(sorting ?? [{ "id": "idLab", "desc": true }]));
            url.searchParams.set('isAdmin', isAdmin);

            const response = await fetch(url.href, {
                method: 'GET',
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Network request failed');
            }
            return response.json(); // Restituisce dati puri
        },
        initialPageParam: 0,
        getNextPageParam: (_lastGroup, groups) => groups.length,
        refetchOnWindowFocus: false,
    });

    const rawData = useMemo(
        () => queryResult.data?.pages.flatMap((page) => page.data) ?? [],
        [queryResult.data]
    );

    const flatData = useMemo(() => {
        return rawData.map(lab => {
            const progress = calculateProgress(lab);
            return {
                ...lab,
                createdAt: convertDate(lab.createdAt),
                nextActivity: formatNextActivity(lab.usageData?.schedulerLab),
                statusChartProps: {
                    variant: progress.progressVariant,
                    now: progress.progressCount,
                    label: progress.progressLabel,
                },
            };
        });
    }, [rawData]);

    const totalDBRowCount = queryResult.data?.pages?.[0]?.meta?.totalRowCount ?? 0;
    const totalFetched = flatData.length;

    return {
        ...queryResult,
        flatData,
        totalDBRowCount,
        totalFetched,
    };
};
